<template>
  <div>
    <Breadcrumb :items="breadcrumbs" />
    <Banner
      title="Bookings"
      subtitle="This list shows your booking and order history."
    >
      <ActionButton
        label="New Booking"
        :theme="{ primary, secondary, event, danger }"
        class="w-auto ml-auto"
        :style="{
          fontSize: '1rem',
          padding: '.78571rem 1.5rem'
        }"
        @customEvent="isOpenMakeBooking = true"
      />
    </Banner>
    <Record :current="current" :total="total" :loading="loading">
      <RecordRow v-for="item in record" :key="item.id" :data="item">
        <RecordData>
          {{ item.code }}
        </RecordData>
        <RecordData>
          <span class="badge badge-danger" v-if="item.cancelledAt">
            Cancelled
          </span>
          <span class="badge badge-success" v-else-if="item.paidAt">
            Paid
            <template v-if="handlePaymentMethod(item.id)">
              via {{ handlePaymentMethod(item.id).value }}
            </template>
          </span>
          <span class="badge badge-warning" v-else>Unpaid</span>
        </RecordData>
        <RecordData class="-action">
          <router-link
            tag="a"
            :to="'/user/orders/invoice/' + item.id"
            class="badge badge-light badge-custom mr-10"
            :style="{ border: `1px solid ${primary}`, color: primary }"
            @mouseenter.native="mouseHover(true, $event)"
            @mouseleave.native="mouseHover(false, $event)"
          >
            View
          </router-link>
          <span
            :class="item.cancelledAt || item.paidAt ? 'badge-disabled' : ''"
            class="badge badge-danger badge-custom"
            @click="
              handleCancelInvoice(
                item.id,
                item.cancelledAt || item.paidAt,
                item.code
              )
            "
            >Cancel</span
          >
        </RecordData>
      </RecordRow>
      <RecordRow v-if="!record.length">
        No result found.
      </RecordRow>
    </Record>

    <MakeBookingModal
      v-if="isOpenMakeBooking"
      @close="isOpenMakeBooking = false"
    />
  </div>
</template>

<script>
import MakeBookingModal from "@/components/modals/MakeBookingModal";
import Banner from "@/components/banner/TwoColumns.vue";
import Breadcrumb from "@/components/Breadcrumb";
import Record from "@/components/record/Record";
import RecordRow from "@/components/record/RecordRow";
import RecordData from "@/components/record/RecordData";
import ActionButton from "@/components/ActionButton";
import {
  bookingCancelEmail,
  bookingCancelEmailToOwner
} from "@/helpers/mailer";

import * as logger from "@/helpers/logger";
import axios from "@/helpers/axios";
import _ from "lodash";
import { dateToday } from "@/helpers/date";

export default {
  components: {
    ActionButton,
    Banner,
    Breadcrumb,
    MakeBookingModal,
    Record,
    RecordRow,
    RecordData
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    }
  },
  data() {
    return {
      // App State
      mounted: false,
      loading: false,
      isOpenMakeBooking: false,

      // Table States
      total: null,
      limit: 20,
      current: 1,
      record: [],
      breadcrumbs: [
        { path: "/user", name: "Home" },
        { path: "", name: "Orders" }
      ],
      userDetails: this.$store.getters.getUser
    };
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll, false);
  },
  calculated: {},
  mounted() {
    this.mounted = true;
    this.getRecord();

    window.addEventListener("scroll", this.handleScroll, false);
  },
  methods: {
    handleCancelInvoice(id, cond, code) {
      if (!cond) {
        this.$swal({
          title: "Are you sure you want to cancel this order?",
          text: code,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes",
          confirmButtonColor: "#e15a4e",
          cancelButtonText: "No",
          reverseButtons: true
        }).then(async result => {
          if (result.value) {
            await axios
              .patch("/orders/" + id, {
                cancelledAt: dateToday()
              })
              .then(response => {
                logger.log(response);
                this.current = 1;
                this.record = [];
                this.getRecord();
              });

            let specRecord = await axios.get(`/orders/${id}`).then(result => {
              return result.status == 200 ? result.data.data : [];
            });
            bookingCancelEmail({
              to: specRecord.User.email,
              displayName: specRecord.User.displayName,
              order: specRecord
            });

            bookingCancelEmailToOwner({
              fullName:
                specRecord.User.firstName + " " + specRecord.User.lastName,
              order: specRecord
            });
          }
        });
      }
    },
    handlePaymentMethod(id) {
      const OrderMeta = _.find(this.record, { id: id }).OrderMeta;
      const paymentMethod = _.find(OrderMeta, { key: "paymentMethod" });
      if (paymentMethod) {
        return paymentMethod;
      }
      return null;
    },
    getRecord() {
      this.loading = true;
      axios
        .get(
          `/orders?page[number]=${this.current}&page[size]=${this.limit}&userId=${this.$store.getters.getUser.id}`
        )
        .then(response => {
          this.record = [...this.record, ...response.data.data];
          this.total = response.data.meta.totalCount;
        })
        .catch(error => logger.error(error))
        .then(() => (this.loading = false));
    },
    mouseHover(param, $event) {
      $event.target.style.backgroundColor = param ? this.secondary : "#fff";
    },
    handleScroll: _.debounce(function() {
      if (
        window.innerHeight + window.pageYOffset >=
        document.body.scrollHeight
      ) {
        if (this.total > this.current) {
          this.current += 1;
          this.getRecord();
        }
      }
    }, 10)
  }
};
</script>

<style lang="scss" scoped>
.badge {
  padding: 8px 20px;
  font-weight: 500;
  transition: all 0.3s ease;
  &-custom {
    font-size: 13px;
    height: 30px;
    line-height: 16px;
    border-radius: 15px;
    -webkit-border-radius: 15px;
    -moz-border-radius: 15px;
    cursor: pointer;
  }
  &-disabled {
    pointer-events: none !important;
    background: $gray-200 !important;
  }
  &-light {
    background-color: #ffffff;
    color: #51b67e;
  }
  &-success {
    background-color: #67c039;
  }
  &-warning {
    background-color: #ffca28;
  }
}
</style>
